import { Injectable } from '@angular/core';
import {
  PdfAnnotation,
  ANNOTATION_CLASS,
  PAGE_SELECTOR,
  AnnotationType,
} from './gk-document-preview-model';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GkDocumentPreviewService {
  public $loading = new BehaviorSubject<boolean>(false);

  renderAnnotations(
    iframeDoc: Document,
    annotations: PdfAnnotation[],
    onClick: (event: MouseEvent, marker: PdfAnnotation) => void,
  ): void {
    const pages = iframeDoc.querySelectorAll(
      `.${PAGE_SELECTOR}`,
    ) as NodeListOf<HTMLElement>;
    if (!pages.length || !annotations.length) {
      return;
    }

    this.preparePages(pages);

    const adjustedMarkers = this.adjustMarkersWhenOverlap(annotations);

    for (const marker of adjustedMarkers) {
      const pageEl = pages[marker.page - 1];
      if (!pageEl) continue;

      const element = this.createAnnotation(iframeDoc, marker);
      this.attachAnnotationInteractions(element);
      this.addClickHandler(element, marker, onClick);
      pageEl.appendChild(element);
    }
  }

  hideAllHighlights(iframeDoc: Document): void {
    const allHighlights = iframeDoc.querySelectorAll<HTMLDivElement>(
      `.${ANNOTATION_CLASS}[data-type="${AnnotationType.Highlight}"]`,
    );
    allHighlights.forEach((el) => {
      el.style.display = 'none';
    });
  }

  showMatchingHighlight(iframeDoc: Document, markerId: string): void {
    const matchingHighlight = iframeDoc.querySelector<HTMLDivElement>(
      `.${ANNOTATION_CLASS}[data-id="${markerId}"][data-type="${AnnotationType.Highlight}"]`,
    );

    if (matchingHighlight) {
      matchingHighlight.style.display = 'block';
    }
  }

  private adjustMarkersWhenOverlap(
    annotations: PdfAnnotation[],
  ): PdfAnnotation[] {
    return annotations.reduce<PdfAnnotation[]>((acc, annotation) => {
      if (annotation.type !== AnnotationType.Marker) {
        return [...acc, annotation];
      }

      const adjustedMarker = this.adjustMarkerPosition(annotation, acc);
      return [...acc, adjustedMarker];
    }, []);
  }

  private adjustMarkerPosition(
    marker: PdfAnnotation,
    existing: PdfAnnotation[],
  ): PdfAnnotation {
    let adjustedLeft = marker.left;

    while (this.hasOverlap(marker, adjustedLeft, existing)) {
      adjustedLeft += 1;
    }

    return { ...marker, left: adjustedLeft };
  }

  private hasOverlap(
    marker: PdfAnnotation,
    left: number,
    existing: PdfAnnotation[],
  ): boolean {
    return existing.some(
      (m) =>
        m.type === AnnotationType.Marker &&
        m.page === marker.page &&
        m.left === left &&
        this.isOverlapping(m, marker),
    );
  }

  isOverlapping(a: PdfAnnotation, b: PdfAnnotation): boolean {
    return (
      a.page === b.page &&
      !(a.top + a.height <= b.top || b.top + b.height <= a.top)
    );
  }

  removeAnnotations(iframeDoc: Document): void {
    const annotations = iframeDoc.querySelectorAll(`.${ANNOTATION_CLASS}`);
    annotations.forEach((element) => element.remove());
  }

  private preparePages(pages: NodeListOf<HTMLElement>): void {
    pages.forEach((page) => {
      page.style.position = 'relative';
    });
  }

  private createAnnotation(
    doc: Document,
    marker: PdfAnnotation,
  ): HTMLDivElement {
    const element = doc.createElement('div');
    element.classList.add(ANNOTATION_CLASS);
    element.dataset['id'] = marker.id;
    element.dataset['type'] = marker.type;

    const isHighlight = marker.type === AnnotationType.Highlight;

    Object.assign(element.style, {
      position: 'absolute',
      left: `${marker.left}%`,
      top: `${marker.top}%`,
      width: `${marker.width}%`,
      height: `${marker.height}%`,
      backgroundColor: marker.color,
      border: `${marker.border} border-1`,
      opacity: '0.4',
      pointerEvents: 'auto',
      cursor: 'pointer',
      borderRadius: '3px',
      display: isHighlight ? 'none' : 'block',
    });

    return element;
  }

  private handlePointerEnter = (
    el: HTMLDivElement,
    type: AnnotationType,
  ): void => {
    if (type === AnnotationType.Marker) el.style.opacity = '1';
    if (type === AnnotationType.Highlight) {
      el.style.opacity = '0.6';
      el.style.border = '2px dashed grey';
    }
    el.style.boxShadow = '0 0 8px rgba(0, 122, 204, 0.5)';
  };

  private handlePointerLeave = (el: HTMLDivElement): void => {
    el.style.opacity = '0.4';
    el.style.boxShadow = 'none';
    el.style.border = 'none';
  };

  private handleClick = (el: HTMLDivElement, type: AnnotationType): void => {
    if (type !== AnnotationType.Highlight) return;

    el.style.transform = 'translateY(1px)';
    el.style.boxShadow = `
      rgba(50, 50, 93, 0.25) 0px 0px 30px inset,
      rgba(0, 0, 0, 0.3) 0px 0px 15px inset
    `;

    setTimeout(() => {
      el.style.transform = '';
      el.style.boxShadow = el.matches(':hover')
        ? '0 0 8px rgba(0, 122, 204, 0.5)'
        : 'none';
    }, 1);
  };

  private attachAnnotationInteractions(element: HTMLDivElement): () => void {
    const type = element.dataset['type'] as AnnotationType;

    const enter = (): void => this.handlePointerEnter(element, type);
    const leave = (): void => this.handlePointerLeave(element);
    const click = (): void => this.handleClick(element, type);

    element.addEventListener('pointerenter', enter);
    element.addEventListener('pointerleave', leave);
    element.addEventListener('click', click);

    return () => {
      element.removeEventListener('pointerenter', enter);
      element.removeEventListener('pointerleave', leave);
      element.removeEventListener('click', click);
    };
  }

  private addClickHandler(
    element: HTMLDivElement,
    marker: PdfAnnotation,
    onClick: (event: MouseEvent, marker: PdfAnnotation) => void,
  ): void {
    element.addEventListener('click', (event: MouseEvent) =>
      onClick(event, marker),
    );
  }
}
